import React, { useState } from 'react';
import { Alert, UncontrolledAlertProps } from 'reactstrap';

/**
 * A UI component that displays error information in a user-friendly format.
 * 
 * Depending on the `error.code`, it renders different layouts. For example:
 * - If `error.code` is `'AKC-VAL-001'`, it shows a list of validation errors.
 * - Otherwise, it displays the error message.
 *
 * @component
 * @param {object} props - Component props.
 * @param {Error & {code?: string, data?: any}} props.error - The error object containing optional `code` and `data`.
 * 
 * @returns {JSX.Element} The rendered error UI.
 */
export const ErrorUI = ({
  error
}: {
  error: Error & {code?: string, data?: any}
}) => {
  switch (error?.code) {
    case 'AKC-VAL-001':
      return (
        <>
          <h4>Validation Error</h4>
          <ul>
            {Object.entries(error.data ?? {}).map(([k, v]) => (
              <li key={k}>
                <b>{k}</b>: {v as any}
              </li>
            ))}
          </ul>
        </>
      );
    default:
      return (
        <>
          <h5>{error.message || `${error}`}</h5>
        </>
      );
  }
};


/**
 * A notification alert component that optionally displays an error.
 * 
 * If an `error` is provided, the component uses the `ErrorUI` component
 * to render a user-friendly error message. Otherwise, it displays the `children`.
 *
 * This component wraps a `reactstrap` `Alert` component, supporting its props.
 * 
 * @component
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The content of the notification (ignored if `error` is provided).
 * @param {Error} [props.error] - An error object to display. If present, replaces `children` with an `ErrorUI`.
 * @param {Function} [props.onClose] - Optional callback triggered when the alert is closed.
 * @param {UncontrolledAlertProps} props - Additional props passed to the underlying `Alert` component.
 * 
 * @returns {JSX.Element} The rendered notification component.
 */
function Notification({
  children,
  error,
  ...props
}: {
  children?: React.ReactNode,
  error?: Error,
} & UncontrolledAlertProps) {
  const [show, setShow] = useState(true);

  if (error) {
    children = <ErrorUI error={error} />;
  }

  return (
    <Alert
      isOpen={show}
      toggle={
        props.onClose
          ? () => {
              props.onClose?.();
              setShow(prev => !prev);
            }
          : undefined
      }
      color={error && 'danger'}
      {...props}
    >
      {children}
    </Alert>
  );
}

export default Notification;
